import { createModel, RematchDispatch } from '@rematch/core';
import produce from 'immer';
import { message } from 'antd';

import { DEFAULT_PAGE_SIZE } from '../../commons/constants/constants';
import { storeName as tourGuideEntityStoreName } from '../entities/tourGuide.entity';
import { RootModel } from '../models';
import { uploadFiles } from '../../services/uploader.service';
import { createTourSound, updateTourSound } from '../../services/tourGuide.service';
import { TourSoundTypeEnum } from '../../types/TourSoundTypeEnum';
import { TourSoundGenreEnum } from '../../types/TourSoundGenreEnum';
import { PageRequest } from '../../types/CommonTypes';

export const storeName = 'tourBgSounds';

const initialState = {
  ids: [],
  pagination: {
    pageable: null,
    sort: null,
    totalPages: 0,
    totalElements: 0,
    last: false,
    first: true,
    numberOfElements: 0,
    size: DEFAULT_PAGE_SIZE,
    number: 0,
    empty: null,
  },
  loading: false,
  bgSoundUploadModalVisible: false,
  editId: null,
  data: [],
  criteria: {},
};

export const tourBgSounds: any = createModel<RootModel>()({
  name: storeName,
  state: initialState,
  reducers: {
    setLoading: produce((state, payload) => {
      state.loading = payload;
    }),
    setIds: produce((state, payload) => {
      state.ids = payload;
    }),
    setEditId: produce((state, payload) => {
      state.editId = payload;
    }),
    setBgSoundUploadModalVisible: produce((state, payload) => {
      state.bgSoundUploadModalVisible = payload;
    }),
    hideBgSoundUploadModal: produce((state, payload) => {
      state.bgSoundUploadModalVisible = false;
      state.editId = null;
    }),
    setPagination: produce((state, payload) => {
      state.pagination = payload;
    }),
    setCriteria: produce((state, payload) => {
      state.criteria = payload;
    }),
  },
  effects: (dispatch: RematchDispatch<typeof tourBgSounds>) => ({
    async reloadList(payload, state) {
      const { criteria, pagination } = state[storeName];
      await dispatch.tourBgSounds.loadTourBgSounds({ ...pagination, ...criteria });
    },
    async resetFilter(payload, state) {
      const { pagination } = state[storeName];
      await dispatch.tourBgSounds.loadTourBgSounds({ ...pagination, page: 0 });
    },
    async loadTourBgSounds(payload, state) {
      const params = {
        ...payload,
        page: payload ? payload.page : state[storeName].pagination.number,
        size: payload ? payload.size : state[storeName].pagination.size,
      };
      const data = await dispatch[tourGuideEntityStoreName].loadBgSound(params);
      if (data && data.content) {
        this.setIds(data.content.map((tourBgSound) => tourBgSound.id));
        this.setPagination((({ content, ...otherProps }) => otherProps)(data));
        this.setCriteria(params);
      }
    },
    async saveTourBgSound({ formData, userId }) {
      dispatch.tourBgSounds.setLoading(true);
      try {
        let uploadedSound = null;
        if (formData.audioFile instanceof File) {
          const fileResult = await uploadFiles(
            null,
            [formData.audioFile],
            'toursound',
            userId,
            'media'
          );
          if (fileResult.filesList.length > 0) {
            const fileStorage = fileResult.filesList[0];
            uploadedSound = {
              id: fileStorage.id,
            };
          }
        }

        if (formData?.id) {
          await updateTourSound(formData.id, {
            ...formData,
            sound: uploadedSound,
          });
          dispatch.tourBgSounds.reloadList();
        } else {
          await createTourSound({
            type: TourSoundTypeEnum.BG_SOUND,
            genre: TourSoundGenreEnum.MP3_FILE,
            ...formData,
            sound: uploadedSound,
          });
          dispatch.tourBgSounds.resetFilter();
        }

        message.success('Save Tour BgSound Successfully');
      } catch (e) {
        console.error(e);
        message.error('Tour BgSound Saving Failure!');
      } finally {
        dispatch.tourBgSounds.setLoading(false);
      }
    },
    async pagingAsync(payload: PageRequest, state) {
      const { criteria = {}, pagination = {} } = state[storeName];
      await this.loadTourBgSounds({ ...pagination, ...criteria, ...payload });
    },
  }),
});

export default tourBgSounds;
